import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { searchVictim, searchGroup, getGroupVictims } from '@/lib/api/ransomware';
import type { RansomwareGroup, SearchType, RansomwareAttack, TTP, Technique, Vulnerability } from '@/lib/types/ransomware';
import { ExternalLink, Info, ShieldAlert, ListTree, Wrench, CalendarDays, Users, FileText } from 'lucide-react';

interface RansomwareSearchResultsProps {
  query: string;
  type: SearchType;
}

function cleanHtmlTags(text: string) {
  return text.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
}


function formatDate(dateStr: string | undefined): string {
  if (!dateStr) return 'Unknown Date';

  try {
    // Handle ISO date strings
    if (dateStr.includes('T')) {
      return new Date(dateStr).toLocaleDateString('en-GB');
    }

    // Handle YYYY-MM-DD format
    if (dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
      return new Date(dateStr).toLocaleDateString('en-GB');
    }

    // Handle DD/MM/YYYY format
    if (dateStr.includes('/')) {
      const [day, month, year] = dateStr.split('/').map(Number);
      const date = new Date(year, month - 1, day);
      if (!isNaN(date.getTime())) {
        return date.toLocaleDateString('en-GB');
      }
    }

    // If date is a valid timestamp
    const timestamp = Date.parse(dateStr);
    if (!isNaN(timestamp)) {
      return new Date(timestamp).toLocaleDateString('en-GB');
    }

    return 'Invalid Date';
  } catch (error) {
    console.error('Error formatting date:', error);
    return 'Invalid Date';
  }
}

function VictimCard({ victim }: { victim: RansomwareAttack }) {
  return (
    <Card className="w-full overflow-hidden">
      <CardHeader className="overflow-hidden">
        <CardTitle className="text-lg truncate" title={victim.title || victim.victim}>
          {victim.title || victim.victim}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-2">
          {/* Adversary Group */}
          {victim.group_name && (
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground shrink-0">Adversary Group:</span>
              <span className="text-sm truncate">{victim.group_name}</span>
            </div>
          )}
          
          {/* Domain */}
          {victim.domain && (
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground shrink-0">Domain:</span>
              <span className="text-sm font-medium truncate">{victim.domain}</span>
            </div>
          )}
          
          {/* Incident Date */}
          {victim.date && (
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground shrink-0">Incident Date:</span>
              <span className="text-sm">{formatDate(victim.date)}</span>
            </div>
          )}
          
          {/* Date Reported */}
          {victim.added && (
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground shrink-0">Date Reported:</span>
              <span className="text-sm">{formatDate(victim.added)}</span>
            </div>
          )}
          
          {/* Country */}
          {victim.country && (
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground shrink-0">Country:</span>
              <span className="text-sm truncate">{victim.country}</span>
            </div>
          )}
          
          {/* Activity Sector */}
          {victim.activity && victim.activity !== 'Not Found' && (
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground shrink-0">Activity Sector:</span>
              <span className="text-sm truncate">{victim.activity}</span>
            </div>
          )}
          
          {/* Source URL */}
          {victim.url && (
            <div className="pt-2">
              <a
                href={victim.url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
              >
                View Source
                <ExternalLink className="h-3 w-3" />
              </a>
            </div>
          )}
          
          {/* Summary/Description */}
          {victim.summary && (
            <div className="pt-2">
              <p className="text-sm text-muted-foreground line-clamp-3">
                {cleanHtmlTags(victim.summary)}
              </p>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export function RansomwareSearchResults({ query, type }: RansomwareSearchResultsProps) {
  const [victims, setVictims] = useState<RansomwareAttack[]>([]);
  const [group, setGroup] = useState<RansomwareGroup | null>(null);
  const [groupVictims, setGroupVictims] = useState<RansomwareAttack[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;

    async function search() {
      if (!query) return;
      
      setIsLoading(true);
      setError(null);

      try {
        if (type === 'victim') {
          const results = await searchVictim(query);
          if (isMounted) {
            setVictims(results);
            setGroup(null);
            setGroupVictims([]);
          }
        } else {
          const [groupResult, victims] = await Promise.all([
            searchGroup(query),
            getGroupVictims(query)
          ]);

          if (isMounted) {
            setGroup(groupResult);
            setVictims([]);
            setGroupVictims(victims);
          }
        }
      } catch (err) {
        if (err instanceof Error && err.message === 'Authentication failed') {
          // Potentially handle auth failure, e.g., redirect to login
          // For now, just return to prevent further processing
          console.warn('Authentication failed during ransomware search.');
          if (isMounted) setError('Authentication failed. Please log in again.');
          return;
        }
        if (isMounted) {
          setError('Error performing search');
          console.error('Search error:', err instanceof Error ? err.message : String(err));
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    }

    search();

    return () => {
      isMounted = false;
    };
  }, [query, type]);

  if (isLoading) {
    return (
      <div className="mx-auto">
        <Card>
          <CardContent className="p-6">
            <div className="text-center text-muted-foreground">Loading...</div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mx-auto">
        <Card>
          <CardContent className="p-6">
            <div className="text-center text-destructive">{error}</div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (type === 'victim') {
    if (victims.length === 0) {
      return (
        <div className="mx-auto">
          <Card>
            <CardContent className="p-6">
              <div className="text-center text-muted-foreground">No results found</div>
            </CardContent>
          </Card>
        </div>
      );
    }

    return (
      <div className="mx-auto">
        <div className="grid gap-4 sm:grid-cols-1 lg:grid-cols-2 xl:grid-cols-3">
          {victims.map((victim, index) => (
            <VictimCard key={index} victim={victim} />
          ))}
        </div>
      </div>
    );
  }

  if (type === 'group') {
    if (!group) {
      return (
        <div className="mx-auto">
          <Card>
            <CardContent className="p-6">
              <div className="text-center text-muted-foreground">No results found</div>
            </CardContent>
          </Card>
        </div>
      );
    }

    return (
      <div className="mx-auto space-y-6">
        <Card>
          <CardHeader>
            <div className="flex justify-between items-start">
              <CardTitle className="text-3xl font-bold text-primary">{group.group.toUpperCase()}</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="p-6 space-y-8">
            {/* Description */}
            <div className="space-y-2">
              <h3 className="text-xl font-semibold flex items-center"><Info size={20} className="mr-2 text-blue-500" /> Overview</h3>
              <p className="text-sm text-muted-foreground leading-relaxed" dangerouslySetInnerHTML={{ __html: group.description }} />
            </div>

            {/* Key Details Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <Card className="bg-muted/30">
                <CardHeader><CardTitle className="text-lg flex items-center"><CalendarDays size={18} className="mr-2 text-green-500"/>Timestamps</CardTitle></CardHeader>
                <CardContent className="space-y-1 text-sm">
                  {group.firstseen && <p><strong>First Seen:</strong> {formatDate(group.firstseen)}</p>}
                  {group.lastseen && <p><strong>Last Seen:</strong> {formatDate(group.lastseen)}</p>}
                  {group.added_date && <p><strong>Profile Added:</strong> {formatDate(group.added_date)}</p>}
                </CardContent>
              </Card>
              <Card className="bg-muted/30">
                <CardHeader><CardTitle className="text-lg flex items-center"><Users size={18} className="mr-2 text-red-500"/>Victimology</CardTitle></CardHeader>
                <CardContent className="space-y-1 text-sm">
                  <p><strong>Total Victims:</strong> <Badge variant="destructive">{group.victims}</Badge></p>
                </CardContent>
              </Card>
              <Card className="bg-muted/30">
                <CardHeader><CardTitle className="text-lg flex items-center"><FileText size={18} className="mr-2 text-purple-500"/>Ransom Notes & Negotiations</CardTitle></CardHeader>
                <CardContent className="space-y-1 text-sm">
                  <p><strong>Has Ransom Notes:</strong> {group.has_ransomnote ? `Yes (${group.ransomnotes_count})` : 'No'}</p>
                  <p><strong>Has Negotiations:</strong> {group.has_negotiations ? `Yes (${group.negotiation_count})` : 'No'}</p>
                </CardContent>
              </Card>
            </div>

            {/* Locations */}
            {group.locations && group.locations.length > 0 && (
              <div className="space-y-3">
                <h3 className="text-xl font-semibold flex items-center"><ListTree size={20} className="mr-2 text-indigo-500" /> Group Sites</h3>
                <div className="space-y-2">
                  {group.locations.map((location, index) => (
                    <div key={index} className="text-sm font-mono bg-muted/50 p-3 rounded-md flex items-center justify-between shadow-sm">
                      <a href={location.slug} target="_blank" rel="noopener noreferrer" className="break-all hover:underline">{location.fqdn}</a>
                      <Badge variant={location.available ? 'default' : 'secondary'} className="ml-3 flex-shrink-0">
                        {location.available ? 'Active' : 'Inactive'}
                      </Badge>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Tools */}
            {group.tools && Object.keys(group.tools).length > 0 && (
              <div className="space-y-3">
                <h3 className="text-xl font-semibold flex items-center"><Wrench size={20} className="mr-2 text-orange-500" /> Tools Used</h3>
                <div className="grid gap-x-6 gap-y-4 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
                  {Object.entries(group.tools).map(([category, toolList]) => (
                    toolList && toolList.length > 0 && (
                      <div key={category} className="space-y-1">
                        <h4 className="text-md font-medium text-muted-foreground capitalize">{category.replace(/([A-Z])/g, ' $1').trim()}</h4>
                        <div className="flex flex-wrap gap-2">
                          {toolList.map((tool) => (
                            <Badge key={tool} variant="outline" className="text-xs">
                              {tool}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )
                  ))}
                </div>
              </div>
            )}

            {/* TTPs */}
            {group.ttps && group.ttps.length > 0 && (
              <div className="space-y-3">
                <h3 className="text-xl font-semibold flex items-center"><ListTree size={20} className="mr-2 text-teal-500" /> Tactics, Techniques, and Procedures (TTPs)</h3>
                {group.ttps.map((ttp: TTP) => (
                  <Card key={ttp.tactic_id} className="bg-muted/30">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-lg">{ttp.tactic_name} <Badge variant="secondary" className="ml-2">{ttp.tactic_id}</Badge></CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2 pt-2">
                      {ttp.techniques.map((technique: Technique) => (
                        <div key={technique.technique_id} className="p-2 border-l-4 border-primary/50 bg-background/50 rounded-r-md">
                          <p className="font-semibold text-sm">{technique.technique_name} <Badge variant="outline" className="text-xs">{technique.technique_id}</Badge></p>
                          <p className="text-xs text-muted-foreground">{technique.technique_details}</p>
                        </div>
                      ))}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}

            {/* Vulnerabilities */}
            {group.vulnerabilities && group.vulnerabilities.length > 0 && (
              <div className="space-y-3">
                <h3 className="text-xl font-semibold flex items-center"><ShieldAlert size={20} className="mr-2 text-yellow-500" /> Known Vulnerabilities Exploited</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {group.vulnerabilities.map((vuln: Vulnerability, index: number) => (
                    <Card key={`${vuln.CVE}-${index}`} className="bg-muted/30">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-md">{vuln.CVE}</CardTitle>
                        <p className="text-xs text-muted-foreground">{vuln.Vendor} - {vuln.Product}</p>
                      </CardHeader>
                      <CardContent className="pt-2">
                        <Badge
                          variant={
                            vuln.severity === 'CRITICAL' ? 'destructive' :
                            vuln.severity === 'HIGH' ? 'warning' :
                            vuln.severity === 'MEDIUM' ? 'default' : // or another color for medium
                            'secondary' // for LOW or other
                          }
                        >
                          {vuln.severity} (CVSS: {vuln.CVSS})
                        </Badge>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}

          </CardContent>
        </Card>

        {groupVictims.length > 0 && (
          <div className="space-y-4">
            <h2 className="text-2xl font-ibm-condensed">Known Victims ({groupVictims.length})</h2>
            <div className="grid gap-4 sm:grid-cols-1 lg:grid-cols-2 xl:grid-cols-3">
              {groupVictims.map((victim, index) => (
                <VictimCard key={index} victim={victim} />
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  return null;
}